import React, { useState } from 'react';
import { Download, FileText, BarChart3, Loader2, AlertTriangle } from 'lucide-react';
import api from '../../services/api';
import toast from 'react-hot-toast';

const Reports = () => {
  const [loading, setLoading] = useState({});

  const downloadReport = async (reportType, filename, contentType) => {
    setLoading(prev => ({ ...prev, [reportType]: true }));
    try {
      const response = await api.get(`/export/reports/${reportType}`, {
        responseType: 'blob'
      });

      // Create download link
      const blob = new Blob([response.data], { type: contentType });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('Report downloaded successfully');
    } catch (error) {
      console.error('Download error:', error);
      toast.error(error.response?.data?.error?.message || 'Failed to download report');
    } finally {
      setLoading(prev => ({ ...prev, [reportType]: false }));
    }
  };

  const reports = [
    {
      id: 'compliance-overview',
      title: 'Compliance Overview',
      description: 'Summary of all verifications and compliance status',
      icon: BarChart3,
      iconBg: 'bg-blue-100',
      iconColor: 'text-blue-600',
      filename: 'compliance-overview.json',
      contentType: 'application/json',
      buttonText: 'Download JSON'
    },
    {
      id: 'monthly-activity',
      title: 'Monthly Activity',
      description: 'Detailed breakdown of monthly verification activity',
      icon: FileText,
      iconBg: 'bg-green-100',
      iconColor: 'text-green-600',
      filename: `monthly-activity-${new Date().getFullYear()}-${new Date().getMonth() + 1}.csv`,
      contentType: 'text/csv',
      buttonText: 'Download CSV'
    },
    {
      id: 'risk-analysis',
      title: 'Risk Analysis',
      description: 'Risk distribution and assessment summary',
      icon: AlertTriangle,
      iconBg: 'bg-purple-100',
      iconColor: 'text-purple-600',
      filename: 'risk-analysis.json',
      contentType: 'application/json',
      buttonText: 'Download JSON'
    }
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Reports</h1>
        <p className="text-gray-600">Generate and export compliance reports</p>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {reports.map(report => {
          const Icon = report.icon;
          const isLoading = loading[report.id];

          return (
            <div key={report.id} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className={`w-12 h-12 ${report.iconBg} rounded-lg flex items-center justify-center mb-4`}>
                <Icon className={`w-6 h-6 ${report.iconColor}`} />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">{report.title}</h3>
              <p className="text-gray-600 text-sm mb-4">{report.description}</p>
              <button
                onClick={() => downloadReport(report.id, report.filename, report.contentType)}
                disabled={isLoading}
                className="flex items-center text-blue-600 font-medium text-sm hover:text-blue-700 disabled:opacity-50"
              >
                {isLoading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Download className="w-4 h-4 mr-2" />
                )}
                {isLoading ? 'Generating...' : report.buttonText}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Reports;
